import React, { useCallback, useMemo, useState } from 'react';
import { FieldFilter } from '@components/FieldFilter';
import { FieldFilterBoxSelectExpression } from '@components/filters/FieldFilterBoxSelectExpression';
import { Filter, filterToRawExpression, MultiSelectTags, OptionObject } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { ellipsis, flex } from '@fabric-ui/theme';
import { iconSize, noop } from '@fabric-ui/utils';
import { Button, Theme } from '@mui/material';
import { useAvContext } from '../../context/AvContextProvider';

interface FieldFilterBoxSelectProps {
  fieldName?: string | null;
  size: 'xSmall' | 'small';
  value: Filter;
  onChange: (filter: Filter) => void;
  activeProjName?: string;
}

const fieldFilterBoxSelectStyle =
  (size: 'xSmall' | 'small', isOpen: boolean) =>
  ({ palette }: Theme) => ({
    ...flex.row,
    ...ellipsis,
    height: size === 'xSmall' ? '24px' : '32px',
    justifyContent: 'start',
    border: `1px solid ${isOpen ? palette.colors.primary[500] : palette.colors.neutrals[400]}`,
    borderRadius: size === 'xSmall' ? '8px' : '10px',
    fontWeight: 400,
    fontSize: size === 'xSmall' ? '12px' : '14px',
    color: palette.colors.neutrals[600],
    width: size === 'xSmall' ? '182px' : '406px',
    py: size === 'xSmall' ? '2px' : '4px',
    pl: '8px',
    gap: '4px',
    ...(size === 'xSmall' ? {} : {}),
    '& > svg': {
      ...iconSize(18),
      position: 'absolute',
      right: '10px',
      color: palette.colors.neutrals[600],
    },
    '&:hover': {
      color: palette.colors.neutrals[600],
      borderColor: isOpen ? palette.colors.primary[500] : palette.colors.neutrals[800],
    },
    '& .MuiChip-root': {
      py: 0,
      pr: '6px',
      backgroundColor: palette.colors.primary[150],
      ...(size === 'xSmall'
        ? {
            pl: '4px',
            gap: '6px',
            '& .MuiChip-label': {
              fontSize: '12px',
              mr: 0,
            },
          }
        : {
            pl: '10px',
          }),
    },
  });

export const FieldFilterBoxSelect = ({
  value,
  onChange,
  fieldName,
  size,
  activeProjName: maybeActiveProjName,
}: FieldFilterBoxSelectProps) => {
  const {
    accountEntities: { fieldMap },
  } = useAvContext();

  const activeProjName = useMemo(
    () => maybeActiveProjName || (fieldName ? fieldMap[fieldName]?.mainProjId?.name : undefined),
    [fieldName, fieldMap, maybeActiveProjName]
  );
  const [isOpen, setIsOpen] = useState(false);
  const rawExpression = useMemo(() => filterToRawExpression(value), [value]);

  const filters = useMemo(
    () => value.expression?.stringCondition?.in?.values ?? (rawExpression ? [encodeURIComponent(JSON.stringify(rawExpression))] : []),
    [value, rawExpression]
  );

  const selectedFilterOptions: OptionObject<string>[] = useMemo(() => filters.map(f => ({ label: f || 'Empty', value: f })), [filters]);

  const handleTagDelete = useCallback(
    (index: number) => {
      if (value.expression?.stringCondition?.in?.values) {
        const newFilterList = value.expression.stringCondition.in.values.filter((_, i) => i !== index);
        onChange({
          ...value,
          expression: {
            ...value.expression,
            stringCondition: newFilterList.length > 0 ? { in: { values: newFilterList } } : { contains: '' },
          },
        });
      }
    },
    [filters, fieldName]
  );

  const getTagProps = useCallback(
    ({ index }: { index: number }) => ({
      onDelete: () => handleTagDelete(index),
    }),
    [handleTagDelete]
  );

  const trigger = useMemo(
    () => (
      <Button sx={fieldFilterBoxSelectStyle(size, isOpen)}>
        {rawExpression && rawExpression?.and?.[0]?.operator !== 'in' ? (
          <FieldFilterBoxSelectExpression rawExpression={rawExpression} key={(rawExpression.and || rawExpression.or)?.length || 0} />
        ) : selectedFilterOptions.length > 0 ? (
          <MultiSelectTags
            tags={selectedFilterOptions}
            getTagProps={getTagProps}
            size={size}
            getLabelFunc={({ label }: any) => label}
            getValueFunc={({ value }: any) => value}
          />
        ) : (
          'Select value'
        )}
        <ArrowDown style={isOpen ? { transform: 'rotate(180deg)' } : {}} />
      </Button>
    ),
    [size, value, rawExpression, selectedFilterOptions, getTagProps]
  );

  return (
    <FieldFilter
      filters={{ [fieldName!]: filters }}
      disabled={!fieldName}
      field={fieldName ?? ''}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isEnabled={!!fieldName}
      onChange={noop}
      onFilterChange={onChange}
      size={size}
      activeProjName={activeProjName}
      trigger={trigger}
      disablePortalInOperatorSelect={false}
    />
  );
};
